import { CandidateReducer, InitialCandidateState } from '../reducer';
import { CandidateAction } from '../types';

describe('CandidateReducer', () => {
  it('should return initial state for unknown action', () => {
    const action = { type: 'UNKNOWN_ACTION' } as any;
    const newState = CandidateReducer(InitialCandidateState, action);
    expect(newState).toEqual(InitialCandidateState);
  });

  describe('SET_SNACKBAR', () => {
    it('should update snackbar state', () => {
      const action: CandidateAction = {
        type: 'SET_SNACKBAR',
        payload: {
          message: 'Test message',
          state: 'error'
        }
      };

      const newState = CandidateReducer(InitialCandidateState, action);

      expect(newState.snackbar).toEqual({
        message: 'Test message',
        state: 'error'
      });
    });
  });

  describe('SET_CAN_SCROLL', () => {
    it('should update can scroll state', () => {
      const action: CandidateAction = {
        type: 'SET_CAN_SCROLL',
        payload: [true, false]
      };

      const newState = CandidateReducer(InitialCandidateState, action);

      expect(newState.candidateApplicationsTableState.canScroll).toEqual([true, false]);
    });
  });

  describe('SET_TABLE_HOVERED', () => {
    it('should update table hovered state', () => {
      const action: CandidateAction = {
        type: 'SET_TABLE_HOVERED',
        payload: true
      };

      const newState = CandidateReducer(InitialCandidateState, action);

      expect(newState.candidateApplicationsTableState.tableHovered).toBe(true);
    });
  });

  describe('SET_SORT', () => {
    it('should update sort settings', () => {
      const action: CandidateAction = {
        type: 'SET_SORT',
        payload: {
          sortBy: 'created_at',
          sortOrder: 'asc'
        }
      };

      const newState = CandidateReducer(InitialCandidateState, action);

      expect(newState.candidateApplicationsTableSettings.sort).toBe('created_at');
      expect(newState.candidateApplicationsTableSettings.sort_order).toBe('asc');
    });
  });

  describe('SET_PAGE', () => {
    it('should update page number', () => {
      const action: CandidateAction = {
        type: 'SET_PAGE',
        payload: 3
      };

      const newState = CandidateReducer(InitialCandidateState, action);

      expect(newState.candidateApplicationsTableSettings.page).toBe(3);
    });
  });

  describe('SET_LIMIT', () => {
    it('should update limit', () => {
      const action: CandidateAction = {
        type: 'SET_LIMIT',
        payload: 25
      };

      const newState = CandidateReducer(InitialCandidateState, action);

      expect(newState.candidateApplicationsTableSettings.limit).toBe(25);
    });
  });

  describe('OPEN_TASK_MODAL', () => {
    it('should update task modal open state', () => {
      const action: CandidateAction = {
        type: 'OPEN_TASK_MODAL',
        payload: true
      };

      const newState = CandidateReducer(InitialCandidateState, action);

      expect(newState.candidateTaskModalIsOpen).toBe(true);
    });
  });

  describe('SET_EDITED_TASK', () => {
    it('should set edited task', () => {
      const mockTask = {
        id: 1,
        subject: 'Test Task',
        note: 'Test note',
        priority: 'High' as const,
        due_at: '2023-12-31',
        completed_at: null,
        remind_mins: 15,
        taskable_id: 1,
        taskable_type: 'Candidate',
        user_id: 1,
        assigned_user: 'John Doe'
      };

      const action: CandidateAction = {
        type: 'SET_EDITED_TASK',
        payload: mockTask
      };

      const newState = CandidateReducer(InitialCandidateState, action);

      expect(newState.editedTask).toEqual(mockTask);
    });

    it('should clear edited task when set to null', () => {
      const mockTask = {
        id: 1,
        subject: 'Test Task',
        note: 'Test note',
        priority: 'High' as const,
        due_at: '2023-12-31',
        completed_at: null,
        remind_mins: 15,
        taskable_id: 1,
        taskable_type: 'Candidate',
        user_id: 1,
        assigned_user: 'John Doe'
      };

      const setAction: CandidateAction = {
        type: 'SET_EDITED_TASK',
        payload: mockTask
      };
      let state = CandidateReducer(InitialCandidateState, setAction);

      const clearAction: CandidateAction = {
        type: 'SET_EDITED_TASK',
        payload: null
      };
      const newState = CandidateReducer(state, clearAction);

      expect(newState.editedTask).toBeNull();
    });
  });

  describe('OPEN_TASK_WORKFLOW_MODAL', () => {
    it('should update task workflow modal open state', () => {
      const action: CandidateAction = {
        type: 'OPEN_TASK_WORKFLOW_MODAL',
        payload: true
      };

      const newState = CandidateReducer(InitialCandidateState, action);

      expect(newState.taskWorkflowModalIsOpen).toBe(true);
    });
  });

  describe('OPEN_EDIT_MODAL', () => {
    it('should update edit modal open state', () => {
      const action: CandidateAction = {
        type: 'OPEN_EDIT_MODAL',
        payload: true
      };

      const newState = CandidateReducer(InitialCandidateState, action);

      expect(newState.openEditModal).toBe(true);
    });
  });

  describe('OPEN_DELETE_MODAL', () => {
    it('should update delete modal open state', () => {
      const action: CandidateAction = {
        type: 'OPEN_DELETE_MODAL',
        payload: true
      };

      const newState = CandidateReducer(InitialCandidateState, action);

      expect(newState.openDeleteModal).toBe(true);
    });
  });

  describe('SET_FOCUSED', () => {
    it('should update focused state', () => {
      const action: CandidateAction = {
        type: 'SET_FOCUSED',
        payload: true
      };

      const newState = CandidateReducer(InitialCandidateState, action);

      expect(newState.focused).toBe(true);
    });
  });

  describe('Multiple state updates', () => {
    it('should handle multiple state updates correctly', () => {
      let state = InitialCandidateState;

      const snackbarAction: CandidateAction = {
        type: 'SET_SNACKBAR',
        payload: { message: 'Success', state: 'success' }
      };
      state = CandidateReducer(state, snackbarAction);

      const pageAction: CandidateAction = {
        type: 'SET_PAGE',
        payload: 2
      };
      state = CandidateReducer(state, pageAction);

      const editModalAction: CandidateAction = {
        type: 'OPEN_EDIT_MODAL',
        payload: true
      };
      state = CandidateReducer(state, editModalAction);

      expect(state.snackbar.message).toBe('Success');
      expect(state.candidateApplicationsTableSettings.page).toBe(2);
      expect(state.openEditModal).toBe(true);
    });
  });
});
